import React from "react";

import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import Chip from "@mui/material/Chip";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import styles from "./TransitionStatusChip.module.css";

interface TransitionStatusChipProps {
  status: string;
  variant?: "transition" | "onboarding" | "sentiment";
}

const TransitionStatusChip: React.FC<TransitionStatusChipProps> = ({
  status,
  variant = "transition",
}): JSX.Element => {
  const getStatusLabel = (status: string, variant: string) => {
    if (variant === "onboarding") {
      switch (status) {
        case "InRecruiting":
          return "In Recruiting";
        case "KickoffComplete":
          return "Kickoff Complete";
        case "Complete":
          return "Complete";
        case "TransitionInProgress":
          return "In Progress";
        case "LaunchComplete":
          return "Launch Complete";
        case "Delayed":
          return "Delayed";
        default:
          return status;
      }
    }

    if (variant === "transition") {
      switch (status) {
        case "OnTrack":
          return "On Track";
        case "Delayed":
          return "Delayed";
        case "OffTrack":
          return "Off Track";
        default:
          return status;
      }
    }

    if (variant === "sentiment") {
      switch (status) {
        case "Good":
          return "Good";
        case "Mixed":
          return "Mixed";
        case "Low":
          return "Low";
        case "AtRisk":
          return "At Risk";
        default:
          return status;
      }
    }

    return status;
  };

  const getStatusClass = (status: string, variant: string) => {
    if (variant === "onboarding") {
      if (status === "LaunchComplete" || status === "Complete") {
        return styles.completed;
      }
      if (status === "Delayed") {
        return styles.delayed;
      }
      if (
        status === "InRecruiting" ||
        status === "Kickoff" ||
        status === "TransitionInProgress"
      ) {
        return styles.inProgress;
      }
    }

    if (variant === "transition") {
      if (status === "OnTrack") {
        return styles.onTrack;
      }
      if (status === "Delayed") {
        return styles.transitionDelayed;
      }
      if (status === "OffTrack") {
        return styles.offTrack;
      }
    }

    if (variant === "sentiment") {
      if (status === "Good") {
        return styles.good;
      }
      if (status === "Mixed" || status === "AtRisk") {
        return styles.mixed;
      }
      if (status === "Low") {
        return styles.risk;
      }
    }

    return styles.default;
  };

  const getStatusIcon = (status: string, variant: string) => {
    if (variant === "onboarding") {
      if (
        status === "InRecruiting" ||
        status === "Kickoff" ||
        status === "TransitionInProgress"
      ) {
        return <CircleIcon className={styles.chipIcon} />;
      }
    }

    if (variant === "transition") {
      return <CircleIcon className={styles.chipIcon} />;
    }

    // For "not started" statuses, we can add an empty circle icon
    if (status === "NotStarted" || status === "Pending") {
      return <CircleOutlinedIcon className={styles.chipIcon} />;
    }

    return null;
  };

  const statusIcon = getStatusIcon(status, variant);

  return (
    <Chip
      classes={{
        root: toClassName(styles.chip, getStatusClass(status, variant)),
        label: styles.chipLabel,
        ...(statusIcon && { icon: styles.chipIcon }),
      }}
      variant="outlined"
      label={getStatusLabel(status, variant)}
      {...(statusIcon && { icon: statusIcon })}
    />
  );
};

export default TransitionStatusChip;
